import React, { useState, useEffect } from 'react';
import { PaperPlaneRight, X, Image as ImageIcon } from '@phosphor-icons/react';

const PasteImagePreviewModal = ({
  isOpen,
  imageFile,
  imageUrl,
  onClose,
  onSend
}) => {
  const [caption, setCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCaption('');
      setIsUploading(false);
    }
  }, [isOpen]);

  if (!isOpen || (!imageFile && !imageUrl)) return null;

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (isUploading) return;

    setIsUploading(true);
    try {
      await onSend({ file: imageFile, caption: caption.trim() });
      onClose();
    } catch (err) {
      console.error('Failed to send pasted image:', err);
    } finally {
      setIsUploading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/75 flex items-center justify-center p-3 animate-in fade-in duration-150">
      <div className="bg-[#111b21] text-white rounded-2xl max-w-lg w-full overflow-hidden shadow-2xl flex flex-col border border-gray-800">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-[#202c33]">
          <div className="flex items-center gap-2">
            <ImageIcon size={20} className="text-[#00a884]" />
            <h3 className="font-semibold text-sm text-gray-200">Send Screenshot</h3>
          </div>
          <button
            onClick={onClose}
            disabled={isUploading}
            className="p-1 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Image Preview Container */}
        <div className="p-4 bg-black/40 flex items-center justify-center min-h-[220px] max-h-[55vh] overflow-hidden relative">
          <img
            src={imageUrl}
            alt="Pasted Screenshot"
            className="max-w-full max-h-[50vh] object-contain rounded-lg shadow-md"
          />
        </div>

        {/* Caption Input & Actions */}
        <form onSubmit={handleSend} className="p-4 bg-[#202c33] flex flex-col gap-3">
          <div className="relative flex items-center bg-[#2a3942] rounded-xl px-3 py-2 border border-gray-700/50 focus-within:border-[#00a884] transition-colors">
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Add a caption... (optional)"
              disabled={isUploading}
              autoFocus
              className="w-full bg-transparent text-sm text-gray-100 placeholder-gray-400 focus:outline-none disabled:opacity-50"
            />
          </div>

          <div className="flex items-center justify-between pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isUploading}
              className="px-4 py-2 text-xs font-semibold text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isUploading}
              className="px-5 py-2 bg-[#00a884] hover:bg-[#008f72] text-white text-xs font-semibold rounded-xl flex items-center gap-2 transition-colors disabled:opacity-50 shadow-md cursor-pointer"
            >
              {isUploading ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Sending...</span>
                </>
              ) : (
                <>
                  <span>Send</span>
                  <PaperPlaneRight size={16} weight="fill" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasteImagePreviewModal;
